import styled from '@emotion/styled';

export type InputStyleStatus = 'active' | 'readonly' | 'valid' | 'invalid';
export type InputStyleSize = 'small' | 'normal' | 'large';

export interface InputStyleProps {
  /**
   * InputStyleStatus indicates the status of the input field
   * one of "active" (default), "readonly", "valid", and "invalid"
   *
   * @default active
   * @type {InputStyleStatus}
   */
  status?: InputStyleStatus;
  /**
   * InputStyleSize indicates the size of the input field
   * one of "normal" (default), "large", and "small"
   *
   * @default normal
   * @type {InputStyleSize}
   */
  sizing?: InputStyleSize;
}

function getBgColorFromStatus(status?: InputStyleStatus) {
  switch (status) {
    case 'readonly':
      return '#F2F2F2';
    case 'valid':
    case 'active':
      return 'white';
    case 'invalid':
      return '#FAE7E9';
    default:
      return 'white';
  }
}
function getBorderColorFromStatus(status?: InputStyleStatus) {
  switch (status) {
    case 'active':
    case 'readonly':
      return '#DDDDDD';
    case 'valid':
      return '#7AB800';
    case 'invalid':
      return '#CE1126';
    default:
      return '#BDCAD2';
  }
}

function getFontSizeFromSizing(sizing?: InputStyleSize) {
  switch (sizing) {
    case 'large':
      return '18px';
    case 'small':
      return '14px';
    case 'normal':
    default:
      return '16px';
  }
}
function getPaddingFromSizing(sizing?: InputStyleSize) {
  switch (sizing) {
    case 'large':
      return '16px 40px 16px 12px';
    case 'small':
      return '8px 36px 8px 10px';
    case 'normal':
    default:
      return '12px 36px 12px 10px';
  }
}

export const InputStyle = styled.input<InputStyleProps>`
  position: relative;
  box-sizing: border-box;
  border-color: ${p => getBorderColorFromStatus(p.status)};
  background-color: ${p => getBgColorFromStatus(p.status)};
  border-width: 2px;
  border-style: solid;
  display: flex;
  align-items: center;
  font-size: ${p => getFontSizeFromSizing(p.sizing)};
  color: #444444;
  padding: ${p => getPaddingFromSizing(p.sizing)};
  min-width: 280px;
  cursor: pointer;
  width: 100%;
  outline-color: transparent;
  &::placeholder {
    font-style: italic;
    color: #dddddd;
  }
  &:disabled {
    color: ${p => (p.status === 'readonly' ? '#777777' : '#BBBBBB')};
    cursor: not-allowed;
    border-color: #dddddd;
    &:hover {
      background-color: ${p => getBgColorFromStatus(p.status)};
    }
  }
  &:hover {
    background-color: #f5fbff;
  }
  &:focus {
    background-color: #f5fbff;
    border-color: #4694ce;
  }
`;

export const InputHintStyle = styled.span`
  display: block;
  color: #566072;
  font-size: 12px;
  margin-bottom: 10px;
`;
